import { motion } from "framer-motion";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ANIMATION_DURATION } from "@/constants/animations.constants";
import { useTranslation } from "@/core/i18n";
import { MobileViewportContainer } from "@/modules/onboarding/components/MobileViewportContainer/MobileViewportContainer";
import { OnboardingBackButton } from "@/modules/onboarding/components/OnboardingBackButton/OnboardingBackButton";
import OnboardingHeaderV3 from "@/modules/onboarding/components/OnboardingScreenHeader/OnboardingHeaderV3";
import { useOnboardingContext } from "@/modules/onboarding/context";
import { Button } from "atoms/button/Button";
import { TextField } from "atoms/text-field/TextField";
import { isValidEmail } from "utils/validators";

const V3EmailPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { profile, updateProfileState } = useOnboardingContext();
  const [email, setEmail] = useState<string>(profile.email ?? "");
  const [invalid, setInvalid] = useState<boolean>(false);

  const handleContinue = useCallback(async () => {
    const valid = isValidEmail(email);
    setInvalid(!valid);
    if (!valid) return;

    updateProfileState({ email: email.trim() });
    navigate("/signin/dob");
  }, [email, navigate, updateProfileState]);

  return (
    <MobileViewportContainer header={<OnboardingBackButton />}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: ANIMATION_DURATION, delay: 0.1 }}
        className="flex w-full max-w-sm flex-col gap-9"
      >
        <OnboardingHeaderV3 title={t("v3EmailPage.title")} align="left" />
        <div className="flex flex-col gap-3">
          <TextField
            type="email"
            label={t("v3EmailPage.label")}
            placeholder={t("v3EmailPage.placeholder")}
            name="email"
            autoComplete="email"
            autoFocus
            value={email}
            invalid={invalid}
            onChange={(value) => {
              setEmail(value);
              if (invalid) setInvalid(false);
            }}
            onBlur={(value) => setInvalid(!isValidEmail(value))}
            labelClassName="hidden"
            className="!ring-0 focus:!ring-0"
          />
          <div className="mt-6">
            <Button fullWidth onClick={handleContinue} disabled={!email || !isValidEmail(email)}>
              {t("common.confirm")}
            </Button>
          </div>
        </div>
      </motion.div>
    </MobileViewportContainer>
  );
};

export default V3EmailPage;
